import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import ApiClient from "@/lib/api-client";
import banner from "../../public/banner.png";
import home1 from "../../public/home1.png";
import home2 from "../../public/home2.png";
import home3 from "../../public/home3.png";
import home4 from "../../public/home4.png";

import { InstructionStep } from "@/components/instruction-step";
import { CoreCommitments } from "@/components/core-commitments";
import { Arrow } from "@/components/arrow-symbol";

interface RegistrationFormData {
  name: string;
  email: string;
  school: string;
  position: string;
}

function Home() {
  const [formData, setFormData] = useState<RegistrationFormData>({
    name: "",
    email: "",
    school: "",
    position: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (
    field: keyof RegistrationFormData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await ApiClient.post("/school-interest-registration", formData);
      setIsSuccess(true);
      setFormData({ name: "", email: "", school: "", position: "" });
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-green-600 text-4xl">✓</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-lg text-gray-600">
            Your registration has been submitted successfully. <br /> We'll be
            in touch soon with more information about the High Impact Class
            program.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="bg-[#0a2540] text-white py-2 flex justify-center">
        <img src={banner} alt="High Impact Class banner" className="h-30" />
      </div>
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <p className="text-lg text-gray-700">
              High Impact Class is a program in development designed to ensure
              students arrive ready to think, equipping teachers with insight
              that sharpens instruction and elevates classroom learning.
            </p>
            <br />
            <p className="text-lg text-gray-700">
              Register your interest to join our closed beta and help shape a
              teacher-centred, responsible use of AI in schools.
            </p>
          </div>

          <div className="max-w-md mx-auto mb-16">
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Register Your School's Interest
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="school">School</Label>
                  <Input
                    id="school"
                    type="text"
                    value={formData.school}
                    onChange={(e) =>
                      handleInputChange("school", e.target.value)
                    }
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    type="text"
                    value={formData.position}
                    onChange={(e) =>
                      handleInputChange("position", e.target.value)
                    }
                    required
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </form>
            </Card>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-12">
              The High Impact Class Instructional Model
            </h2>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-8">
              <InstructionStep
                imageSrc={home1}
                imageBgClass="bg-blue-600"
                gradientClass="from-blue-400 to-white"
                text="Students Prepare for Classroom Learning"
              />

              <Arrow />

              <InstructionStep
                imageSrc={home2}
                imageBgClass="bg-blue-500"
                gradientClass="from-blue-200 to-white"
                text="Students Articulate Understanding with AI"
              />

              <Arrow />

              <InstructionStep
                imageSrc={home3}
                imageBgClass="bg-yellow-500"
                gradientClass="from-yellow-300 to-white"
                text="AI Surfaces Readiness and Misconceptions"
              />

              <Arrow />

              <InstructionStep
                imageSrc={home4}
                imageBgClass="bg-green-600"
                gradientClass="from-green-200 to-white"
                text="Teacher-Led Instruction Drives Deeper Learning"
              />
            </div>
            <br />
            <br />
            <p className="text-left text-lg text-gray-700">
              Artificial intelligence is reshaping education at unprecedented
              speed. Its impact can be profoundly beneficial or deeply
              detrimental.
              <br /> Our commitment is to ensure that its benefits are realised
              thoughtfully, responsibly and at scale.
            </p>

            <CoreCommitments />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
